<template>
  <div class="timeline-item" :class="itemClasses">
    <timeline-item-header
      :start="start"
      :end="end"
      :timezone="timezone"
      :date="date"
      :is-prediction="isPrediction"
      :is-first="isFirst"
    />
    <div class="timeline-item-body">
      <div class="timeline-item-aside">
        <div class="timeline-item-line" v-if="!isPrediction"></div>
        <div class="timeline-item-line-dotted" v-if="isOngoing || isPrediction"></div>
        <div class="timeline-item-start-dot"></div>
        <div class="timeline-item-end-dot"></div>
        <div class="timeline-item-middle-dot" v-if="isOngoing"></div>
      </div>
      <div class="timeline-item-content" :style="contentMargins">
        <timeline-item-card
          :type="item.type || item.event_type"
          :action="action"
          :distance="distance"
          :place="place"
          :duration="duration"
          :moments="item.moments"
          :is-prediction="isPrediction"
          @click.native="goToTrip(item)"
        ></timeline-item-card>
        <timeline-item-tags :items="transportEventTags" v-if="transportEventTags.length > 0"/>
        <timeline-item-tags :items="item.customEvents" v-if="item.customEvents.length > 0"/>
      </div>
    </div>
    <timeline-item-footer v-if="isLast && !isPrediction" :end="end" :date="date"/>
  </div>
</template>

<script>
import { get, has, isObject, flatten, startCase, uniq } from "lodash";
import TimelineItemHeader from "./TimelineItemHeader";
import TimelineItemCard from "./TimelineItemCard";
import TimelineItemTags from "./TimelineItemTags";
import TimelineItemFooter from "./TimelineItemFooter";
import { mstohm } from "utils/time";
import { arrToStr, humanize } from "utils/string";

export default {
  name: "TimelineItem",
  props: {
    item: {
      type: Object,
      required: true
    },
    isFirst: {
      type: Boolean,
      required: true
    },
    isLast: {
      type: Boolean,
      required: true
    }
  },
  components: {
    TimelineItemHeader,
    TimelineItemCard,
    TimelineItemTags,
    TimelineItemFooter
  },
  computed: {
    start() {
      return this.item._start.format("h:mm a");
    },
    end() {
      return this.item._end.format("h:mm a");
    },
    timezone() {
      return this.item._start.format("Z");
    },
    date() {
      return this.item._start.format("Do MMM, YYYY");
    },
    action() {
      if (this.item.type === "Stationary") {
        return get(this.item, "location.significance");
      }

      if (this.item.type === "StationaryPrediction") {
        return get(this.item, "location_type");
      }

      return this.item.mode;
    },
    duration() {
      return mstohm(this.item._duration);
    },
    place() {
      if (
        this.item.type !== "Stationary" &&
        this.item.type !== "StationaryPrediction"
      ) {
        return;
      }

      return getPlace(this.item);
    },
    distance() {
      if (!this.item.distance) {
        return;
      }

      return this.$t("ui.values.km", [
        Math.round((this.item.distance / 1000) * 100) / 100
      ]);
    },
    isOngoing() {
      return this.item.end_ts === null;
    },
    isTransport() {
      return this.item.type === "Transport";
    },
    isPrediction() {
      return (
        this.item.type === "TransportPrediction" ||
        this.item.type === "StationaryPrediction"
      );
    },
    isCarTrip() {
      if (this.isOngoing || this.isPrediction) {
        return false;
      }

      return this.isTransport && this.item.mode === "car";
    },
    itemClasses() {
      return {
        "is-ongoing": this.isOngoing,
        "is-transport": this.isTransport,
        "is-prediction": this.isPrediction,
        "is-car-trip": this.isCarTrip
      };
    },
    contentMargins() {
      if (!this.item._duration) {
        return;
      }

      const duration = this.item._duration / 60000;

      return {
        marginTop: `${duration / 4}px`,
        marginBottom: `${duration / 4}px`
      };
    },
    transportEventTags() {
      const { mode, occupant_role } = this.item;
      let tags = [];

      if (mode !== "car") {
        return tags;
      }

      if (occupant_role) {
        const tag = this.$t(`types.events.occupant_roles.${occupant_role}`);
        tags = [...tags, tag];
      }

      let score = get(this.item, "behavior_scores.overall") , tooltip = '';
      
      if (score && score > 0) {
        score = Math.round(score * 100)
      } else if (score === -1){
        tooltip = this.$t("user.timeline.driving_score_no_value_tooltip")
      }

      const tag = {
        label : this.$t("user.timeline.driving_score"),
        score,
        tooltip
      }

      tags = [...tags, tag];

      return tags;
    },
    customEventTags() {
      const { customEvents } = this.item;

      if (!customEvents || customEvents.length === 0) {
        return [];
      }

      console.log(customEvents);

      const customMeta = customEvents.map(e => {
        const { id, type, _start, _end, end, values } = e;
        const startTime = _start.format("h:mm a");
        const endTime = end ? _end.format("h:mm a") : null;
        const formattedTime = endTime
          ? this.$t("user.timeline.tooltip_time_duration", {
              start: startTime,
              end: endTime
            })
          : this.$t("user.timeline.tooltip_time", { start: startTime });
        const base = {
          id,
          type,
          formattedTime,
          title: null,
          label: null
        };

        if (!isObject(values)) {
          return {
            ...base,
            label: startCase(type)
          };
        }

        return Object.entries(values)
          .map(([key, value]) => {
            if (!value || value.length === 0) {
              return;
            }

            const formattedValue = isNaN(value)
              ? value
              : Math.round(value * 100) / 100;

            return {
              ...base,
              title: type !== key ? startCase(type) : null,
              label: `${startCase(key)}: ${formattedValue}`
            };
          })
          .filter(e => e);
      });

      return flatten(customMeta);
    }
  },
  methods: {
    goToTrip(item) {
      if (!this.isCarTrip) {
        return;
      }

      this.$router.push({
        name: "audience-trip",
        params: { tripStart: item.start }
      });
    }
  }
};

function getPlace(item) {
  const name = get(item, "location.place.name");
  if (name) {
    return name;
  }

  const categories = get(item, "location.place.categories");
  if (categories) {
    return arrToStr(uniq(categories));
  }

  const city = get(item, "location.address.city");
  if (city) {
    return city;
  }

  return;
}
</script>

<style lang="scss" scoped>
@import "~scss/utilities/initial-variables.scss";
@import "~scss/utilities/derived-variables.scss";

$timeline-item-line-color: #323435;
$timeline-item-line-color-transport: $blue;

.timeline-item {
  &.is-transport {
    .timeline-item-line {
      background-color: $timeline-item-line-color-transport;
    }

    .timeline-item-line-dotted {
      border-image-source: url(~assets/img/dots-blue.svg);
    }

    .timeline-item-start-dot,
    .timeline-item-end-dot,
    .timeline-item-middle-dot {
      border-color: $timeline-item-line-color-transport;
    }
  }

  &.is-ongoing {
    .timeline-item-line,
    .timeline-item-line-dotted {
      height: 50%;
    }
  }

  &.is-car-trip {
    .timeline-item-card {
      position: relative;
      padding-right: 1.375rem;
      cursor: pointer;

      &::before {
        content: " ";
        position: absolute;
        top: -1px;
        bottom: -1px;
        right: -1px;
        width: 0.75rem;
        background-color: rgba($black, 0.32);
        border-radius: 1px;
      }

      &::after {
        content: " ";
        position: absolute;
        top: 50%;
        right: 3px;
        width: 0;
        height: 0;
        transform: translateY(-50%);
        border-top: 3px solid transparent;
        border-bottom: 3px solid transparent;
        border-left: 3px solid $white;
        pointer-events: none;
      }
    }
  }
}

.timeline-item-body {
  position: relative;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  padding-top: 0.1875rem;
  padding-bottom: 0.1875rem;
}

.timeline-item-content {
  margin-left: 1.125rem;
}

.timeline-item-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  border-radius: 3px;
  background-color: $timeline-item-line-color;
}

.timeline-item-start-dot,
.timeline-item-end-dot {
  position: absolute;
  left: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: $white;
  border: 1px solid $timeline-item-line-color;
  z-index: 10;
}

.timeline-item-start-dot {
  top: 0;
}

.timeline-item-end-dot {
  bottom: 0;
}

.timeline-item-middle-dot {
  position: absolute;
  width: 14px;
  height: 14px;
  top: 50%;
  margin-left: -4px;
  margin-top: -4px;
  border-radius: 50%;
  background-color: $white;
  border: 3px solid $timeline-item-line-color;
  z-index: 10;
}

.timeline-item-line-dotted {
  position: absolute;
  bottom: 0;
  left: 1px;
  width: 0;
  height: 100%;
  border-width: 0;
  border-right: 4px solid $white;
  border-image-source: url(~assets/img/dots.svg);
  border-image-slice: 4 4;
  border-image-repeat: round;
}
</style>
